import type { LevelDef } from './types.js';

/**
 * The learning run. The Qubit auto-runs forward (+Z) into the screen; the
 * player steers left/right, jumps hurdles, and crouches under bars. History
 * boards line the track. A central wall forks the path (the left/right
 * decision). At the end, the gate: the obvious handle is mounted too high, so
 * operate the reachable corner handle (entangled with it) to switch on the
 * scanner beam, then steer into the beam.
 *
 * Axes: +X right, +Y up, +Z forward. Floor at y = 0.
 */
export const TUTORIAL_LEVEL: LevelDef = {
  id: 'tutorial01',
  spawn: { x: 0, z: 2 },
  trackLength: 120,
  halfWidth: 5,
  floorY: 0,

  obstacles: [
    // Teach the jump: a full-width low hurdle.
    { kind: 'hurdle', xMin: -5, xMax: 5, zMin: 18, zMax: 19, topY: 1.0 },
    // Teach the crouch: a full-width overhead bar.
    { kind: 'bar', xMin: -5, xMax: 5, zMin: 30, zMax: 31, botY: 1.0 },
    // The fork: a central wall — go left or right.
    { kind: 'wall', xMin: -1, xMax: 1, zMin: 42, zMax: 50 },
    // Mixed: hurdle on the left (jump or keep right).
    { kind: 'hurdle', xMin: -5, xMax: 0.5, zMin: 60, zMax: 61, topY: 1.0 },
    // Bar on the right (crouch or keep left).
    { kind: 'bar', xMin: -0.5, xMax: 5, zMin: 72, zMax: 73, botY: 1.0 },
    // Non-lethal warning block in the center before the gate (steer around it).
    { kind: 'wall', xMin: -1.5, xMax: 1.5, zMin: 96, zMax: 97 },
  ],

  // Standing 3D panels at the road edges (x ≈ ±4.6), alternating sides; read as
  // you run alongside them. The two at z=46 sit on opposite branches of the fork.
  boards: [
    { x: -4.6, z: 8, timelineIndex: 0 }, // Planck (left)
    { x: 4.6, z: 26, timelineIndex: 1 }, // Einstein (right)
    { x: -4.6, z: 46, timelineIndex: 5 }, // Schrödinger (left branch)
    { x: 4.6, z: 46, timelineIndex: 7 }, // Entanglement (right branch)
    { x: 4.6, z: 66, timelineIndex: 2 }, // Bohr (right)
    { x: -4.6, z: 84, timelineIndex: 11 }, // Deutsch (left)
    { x: 4.6, z: 100, timelineIndex: 13 }, // Shor (right)
    { x: -4.6, z: 107, timelineIndex: 17 }, // Logical qubits — by the gate (left)
  ],

  gateZ: 110,

  // Obvious handle, dead ahead at the gate but mounted above the jump apex
  // (apex feet-rise ≈ 2.2) — unreachable.
  handleHigh: { x: 0, y: 5, z: 109, reachable: false },
  // Reachable handle in the right corner of the gate — within reach while the
  // gate pins you, so steer right to operate it (then back to the beam).
  handleCorner: { x: 4.2, y: 0.9, z: 109, reachable: true },

  // Scanner beam at the gate threshold; active only after the corner handle latches.
  beam: { x: 0, z: 108.8, w: 3, d: 2.4, h: 3.5 },
};
